import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Landmark, Scale, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";
import { CHANGELOG } from "@/lib/changelogData";
import LenisProvider from "@/components/site/LenisProvider";
import Footer from "@/components/site/Footer";
import Nav from "@/components/site/Nav";
import WhatsAppFab from "@/components/site/WhatsAppFab";
import TextReveal from "@/components/site/TextReveal";
import useSeo from "@/lib/useSeo";

const TYPE_META = {
  subsidy: { label: "Subsidy", Icon: Landmark, ring: "ring-[color:var(--gold)]" },
  policy: { label: "Policy Note", Icon: Scale, ring: "ring-[color:var(--violet)]" },
  update: { label: "Update", Icon: Sparkles, ring: "ring-white/40" },
  release: { label: "Release", Icon: Megaphone, ring: "ring-white/40" },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const groupByYear = (items) =>
  items.reduce((acc, item) => {
    const y = new Date(item.date).getFullYear();
    (acc[y] ||= []).push(item);
    return acc;
  }, {});

const Entry = ({ item, idx }) => {
  const meta = TYPE_META[item.type] || TYPE_META.update;
  const { Icon } = meta;
  return (
    <motion.article
      data-testid={`changelog-entry-${item.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ delay: (idx % 4) * 0.06, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="relative grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 border-t border-white/10 first:border-t-0"
    >
      {/* Date rail */}
      <div className="col-span-12 md:col-span-3 md:sticky md:top-28 md:h-fit">
        <div className="flex md:block items-center gap-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
            {fmtDate(item.date)}
          </p>
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold)] md:mt-1 md:block">
            {meta.label}
          </span>
        </div>
      </div>

      {/* Timeline dot */}
      <div className="hidden md:flex col-span-1 justify-center">
        <div className="relative pt-1">
          <span
            className={`grid h-11 w-11 place-items-center border border-white/15 bg-navy ring-1 ${meta.ring} ring-offset-2 ring-offset-[color:var(--navy)] text-white/90`}
          >
            <Icon className="h-4 w-4" strokeWidth={1.6} />
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="col-span-12 md:col-span-8">
        <h3 className="font-serif tracking-tight text-2xl md:text-4xl leading-[1.08] text-white">
          <TextReveal>{item.title}</TextReveal>
        </h3>
        <p className="mt-5 text-white/75 text-[15px] md:text-[16px] leading-relaxed max-w-2xl">
          <TextReveal>{item.body}</TextReveal>
        </p>
        {item.tags?.length ? (
          <ul className="mt-6 flex flex-wrap gap-2">
            {item.tags.map((t) => (
              <li
                key={t}
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/75 border border-white/20 px-2.5 py-1"
              >
                {t}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </motion.article>
  );
};

const Changelog = () => {
  useSeo({
    title:
      "Changelog — MSMEmoney.ai · Policy notes, subsidy alerts & disbursements",
    description:
      "Running log of MSME policy updates, subsidy disbursements, MSEFC filings and MSMEmoney company news.",
    canonical: "https://msmemoney.ai/changelog",
  });
  const byYear = groupByYear(CHANGELOG);
  const years = Object.keys(byYear).sort((a, b) => Number(b) - Number(a));

  return (
    <LenisProvider>
      <main className="relative bg-navy text-white min-h-screen noise">
        <Nav />

        {/* Header */}
        <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 border-b border-white/10">
          <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
            <Link
              to="/"
              data-testid="changelog-back"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-white/60 hover:text-[color:var(--gold)] transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to home
            </Link>
            <div className="mt-8 grid grid-cols-12 gap-6 items-end">
              <div className="col-span-12 md:col-span-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[color:var(--gold)]">
                  The Ledger · v.1
                </p>
                <h1
                  data-testid="changelog-title"
                  className="mt-5 font-serif tracking-tight text-5xl md:text-7xl leading-[0.95]"
                >
                  <TextReveal>
                    Company updates, policy notes & subsidy alerts.
                  </TextReveal>
                </h1>
              </div>
              <div className="col-span-12 md:col-span-4 md:pb-3">
                <p className="text-white/70 text-[15px] leading-relaxed">
                  A running log of what we&rsquo;ve filed, what&rsquo;s been
                  disbursed, and what changed at the department this week. New
                  entries at the top.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="relative">
          <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12 py-10 md:py-16">
            {years.map((y) => (
              <div key={y} className="relative mb-16 md:mb-24 last:mb-0">
                <div className="sticky top-16 md:top-20 z-10 -mx-5 sm:-mx-8 lg:-mx-12 px-5 sm:px-8 lg:px-12 bg-navy/90 backdrop-blur-md py-3 md:py-4 border-y border-white/10 flex items-baseline gap-6">
                  <span className="font-serif italic text-3xl md:text-4xl text-white">
                    {y}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
                    {byYear[y].length} entr{byYear[y].length === 1 ? "y" : "ies"}
                  </span>
                </div>

                {/* Vertical rule */}
                <div
                  className="hidden md:block absolute left-[calc(25%+1.5rem+1px)] top-16 bottom-0 w-px bg-white/10"
                  aria-hidden
                />

                <div>
                  {byYear[y].map((item, idx) => (
                    <Entry key={item.id} item={item} idx={idx} />
                  ))}
                </div>
              </div>
            ))}

            {/* CTA */}
            <div className="mt-24 border-t border-white/10 pt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <p className="max-w-xl text-white/70 text-sm md:text-[15px]">
                Want subsidy alerts and policy notes emailed to you? Message us
                on WhatsApp with subject &ldquo;Subscribe&rdquo;.
              </p>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-3 bg-[color:var(--gold)] px-6 py-4 text-navy hover:bg-white transition-colors w-fit font-mono text-[11px] uppercase tracking-[0.24em]"
              >
                Get started
              </Link>
            </div>
          </div>
        </section>

        <Footer />
        <WhatsAppFab />
      </main>
    </LenisProvider>
  );
};

export default Changelog;
